
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Loader2, ArrowLeft, Eye, Calendar, Users, FileText } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

// Define a type for a Supabase error response
interface SelectQueryError {
  error: true;
  message?: string;
  [key: string]: any;
}

// Interface for feedback details
interface FeedbackDetail {
  id: string;
  candidate_name: string;
  job_title: string;
  created_at: string;
  feedback_type?: string | null;
}

// Interface for feedback associations with proper types
interface FeedbackAssociation {
  id: string;
  feedback_id: string;
  created_at: string;
  feedback: FeedbackDetail | null;
}

// Interface for request details
interface RequestDetail {
  id: string;
  candidate_name: string;
  job_title: string;
  status: string;
  created_at: string;
}

// Interface for request associations with proper handling of null or error cases
interface RequestAssociation {
  id: string;
  request_id: string;
  created_at: string;
  request: RequestDetail | null;
}

// Type guard to check if an object is a SelectQueryError
function isSelectQueryError(obj: any): obj is SelectQueryError {
  return obj && typeof obj === 'object' && obj.error === true;
}

interface Project {
  id: string;
  name: string;
  description: string | null;
  category: string;
  created_at: string;
}

const ProjectDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [project, setProject] = useState<Project | null>(null);
  const [feedbacks, setFeedbacks] = useState<FeedbackAssociation[]>([]);
  const [requests, setRequests] = useState<RequestAssociation[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id || !user) return;

    const fetchProjectDetails = async () => {
      try {
        setLoading(true);
        
        // Fetch project details
        const { data: projectData, error: projectError } = await supabase
          .from('projects')
          .select('*')
          .eq('id', id)
          .eq('user_id', user.id)
          .single();
        
        if (projectError) {
          console.error('Error fetching project:', projectError);
          toast.error('Erreur lors du chargement du projet');
          navigate('/dashboard');
          return;
        }
        
        setProject(projectData);
        
        // Fetch associated feedbacks
        const { data: feedbacksData, error: feedbacksError } = await supabase
          .from('projects_feedbacks')
          .select(`
            id,
            feedback_id,
            created_at,
            feedback:feedbacks(
              id,
              candidate_name,
              job_title,
              created_at,
              feedback_type
            )
          `)
          .eq('project_id', id)
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });
        
        if (feedbacksError) {
          console.error('Error fetching feedbacks:', feedbacksError);
          toast.error('Erreur lors du chargement des feedbacks associés');
        } else if (feedbacksData) {
          // Filter out any feedbacks where the feedback relation couldn't be found
          const validFeedbacks = feedbacksData
            .filter(item => item.feedback !== null && !isSelectQueryError(item.feedback));
          
          // Type assertion after filtering is now safe
          setFeedbacks(validFeedbacks as FeedbackAssociation[]);
        }
        
        // Prepare an array to hold all valid requests
        let validRequests: RequestAssociation[] = [];
        
        // Fetch associated requests from feedback_requests
        const { data: requestsData, error: requestsError } = await supabase
          .from('projects_requests')
          .select(`
            id,
            request_id,
            created_at,
            request:feedback_requests(
              id,
              candidate_name,
              job_title,
              status,
              created_at
            )
          `)
          .eq('project_id', id)
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });
        
        if (requestsError) {
          console.error('Error fetching requests:', requestsError);
        } else if (requestsData) {
          // First, filter out error responses and null requests 
          const requestsWithoutErrors = requestsData.filter(item => 
            item.request !== null && !isSelectQueryError(item.request)
          );
          
          // Now safely cast to RequestAssociation[] after filtering
          const filteredRequests = requestsWithoutErrors as unknown as RequestAssociation[];
          validRequests = [...validRequests, ...filteredRequests];
        }
        
        // Also fetch from candidate_to_recruiter_feedback_requests
        const { data: candidateRequestsData, error: candidateRequestsError } = await supabase
          .from('projects_requests')
          .select(`
            id,
            request_id,
            created_at,
            request:candidate_to_recruiter_feedback_requests(
              id,
              candidate_name,
              job_title,
              status,
              created_at
            )
          `)
          .eq('project_id', id)
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });
        
        if (candidateRequestsError) {
          console.error('Error fetching candidate requests:', candidateRequestsError);
        } else if (candidateRequestsData) {
          // First, filter out error responses and null requests
          const candidateRequestsWithoutErrors = candidateRequestsData.filter(item => 
            item.request !== null && !isSelectQueryError(item.request)
          );
          
          // Now safely cast to RequestAssociation[] after filtering
          const filteredCandidateRequests = candidateRequestsWithoutErrors as unknown as RequestAssociation[];
          validRequests = [...validRequests, ...filteredCandidateRequests];
        }
        
        // Set the combined list of validated requests
        setRequests(validRequests);
        
      } catch (error) {
        console.error('Error in fetchProjectDetails:', error);
        toast.error('Une erreur inattendue s\'est produite');
      } finally {
        setLoading(false);
      }
    };
    
    fetchProjectDetails();
  }, [id, user, navigate]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const handleViewFeedback = (feedback: FeedbackAssociation) => {
    // Use the correct feedback ID (the one from the feedback object, not the association)
    const feedbackId = feedback.feedback?.id;
    if (feedbackId) {
      navigate(`/view-feedback/${feedbackId}`, { state: { feedbackId } });
    } else {
      toast.error("ID du feedback introuvable");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[300px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!project) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center">
            <p>Projet introuvable ou vous n'avez pas la permission d'y accéder.</p>
            <Button 
              variant="outline" 
              className="mt-4"
              onClick={() => navigate('/dashboard')}
            >
              <ArrowLeft className="mr-2 h-4 w-4" /> Retour
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Filtrer les feedbacks valides (avec données de feedback)
  const validFeedbacks = feedbacks.filter(item => item.feedback !== null);

  return (
    <div className="space-y-6">
      <Button 
        variant="outline" 
        className="mb-4"
        onClick={() => navigate('/dashboard')}
      >
        <ArrowLeft className="mr-2 h-4 w-4" /> Retour
      </Button>
      
      <Card className="overflow-hidden border-0 shadow-xl">
        <div className="bg-gradient-to-r from-purple-700 via-indigo-600 to-blue-600 p-8 text-white">
          <h2 className="text-3xl font-bold mb-3">{project.name}</h2>
          <div className="flex flex-wrap items-center gap-3">
            <Badge className="bg-white/20 hover:bg-white/30 text-white font-medium">
              {project.category}
            </Badge>
            <span className="text-sm opacity-80">
              Créé le {formatDate(project.created_at)}
            </span>
          </div>
          {project.description && (
            <p className="mt-4 text-white/90 text-lg">
              {project.description}
            </p>
          )}
        </div>
      </Card>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="md:col-span-2 border-0 shadow-lg bg-white">
          <CardHeader className="pb-3 border-b">
            <div className="flex items-center">
              <FileText className="h-5 w-5 text-indigo-500 mr-2" />
              <CardTitle>Feedbacks ({validFeedbacks.length})</CardTitle>
            </div>
            <CardDescription>
              Tous les feedbacks associés à ce projet
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-4">
            {validFeedbacks.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground bg-gray-50 rounded-md">
                <FileText className="h-12 w-12 mx-auto mb-3 text-gray-400" />
                <p className="text-lg font-medium">Aucun feedback associé à ce projet</p>
                <p className="text-sm text-gray-500 mt-2">Les feedbacks que vous ajoutez s'afficheront ici</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead>Candidat</TableHead>
                    <TableHead>Poste</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {validFeedbacks.map((feedback) => {
                    // Skip rendering if feedback is null
                    if (!feedback.feedback) return null;
                    
                    return (
                      <TableRow key={feedback.id} className="hover:bg-gray-50">
                        <TableCell className="font-medium">{feedback.feedback.candidate_name || 'Non spécifié'}</TableCell>
                        <TableCell>{feedback.feedback.job_title || 'Non spécifié'}</TableCell>
                        <TableCell>{formatDate(feedback.created_at)}</TableCell>
                        <TableCell>
                          <Badge variant={feedback.feedback.feedback_type === 'candidat' ? 'default' : 'outline'} className={feedback.feedback.feedback_type === 'candidat' ? 'bg-indigo-500' : ''}>
                            {feedback.feedback.feedback_type === 'candidat' ? 'Candidat' : 'Recruteur'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50"
                            onClick={() => handleViewFeedback(feedback)}
                          >
                            <Eye className="h-4 w-4 mr-1" /> Voir
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
        
        <Card className="border-0 shadow-lg bg-white">
          <CardHeader className="pb-3 border-b">
            <div className="flex items-center">
              <Calendar className="h-5 w-5 text-indigo-500 mr-2" />
              <CardTitle>Demandes ({requests.length})</CardTitle>
            </div>
            <CardDescription>
              Demandes de feedback
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-4">
            {requests.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground bg-gray-50 rounded-md">
                <Users className="h-12 w-12 mx-auto mb-3 text-gray-400" />
                <p className="text-lg font-medium">Aucune demande</p>
                <p className="text-sm text-gray-500 mt-2">Les demandes de feedback s'afficheront ici</p>
              </div>
            ) : (
              <div className="space-y-4">
                {requests.map((requestItem) => {
                  // Skip any items where request is null
                  if (!requestItem.request) return null;
                  
                  // Safely access request data
                  const requestData = requestItem.request;
                  
                  return (
                    <div key={requestItem.id} className="p-4 border rounded-md hover:bg-gray-50 transition-colors shadow-sm">
                      <div className="font-medium text-lg">{requestData.candidate_name}</div>
                      <div className="text-sm text-gray-600">{requestData.job_title}</div>
                      <div className="flex justify-between items-center mt-3">
                        <Badge variant={requestData.status === 'completed' ? 'default' : 'outline'} 
                              className={requestData.status === 'completed' ? 'bg-green-500' : ''}>
                          {requestData.status === 'pending' ? 'En attente' : 
                          requestData.status === 'completed' ? 'Complété' : 
                          requestData.status === 'cancelled' ? 'Annulé' : 'Autre'}
                        </Badge>
                        <span className="text-xs text-gray-500">{formatDate(requestItem.created_at)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ProjectDetail;
